// ---------------------------------------------------------------------------
// The charts' shared core, ported once from the design system (R10f)
//
// Every chart helper of the system's r6g-charts block (components/bundle.js,
// "chart" and "CHART_" names), for the chart ports to share: ChartFrame
// (chart-frame.ts), LineChart, BarChart, DonutChart and Heatmap. Each does
// what the system's does, value for value, in the reader's locale (format
// from helpers.ts). The API, in the system's order:
//
// Colour by job, never by rank
//   CHART_SLOTS              chart-1, chart-2, chart-3, chart-5, chart-6: the identity slots, in that order
//   chartInk(color, i)       a named colour wins ('other' is chart-other), else the slot of position i, past the
//                            fifth chart-other (chartInk)
//   chartSeries(ink)         the class that colours a mark: 'sdr-series--' + ink (chartSeries)
// Words
//   chartName(title, label, fallback)  the chart's name as text: a string title, else label, else fallback; a
//                            title given as a template names the figure through its heading, never here (chartName)
//   CHART_DAY, CHART_DAY_LONG, CHART_DAY_TIP, CHART_CLOCK   the date and time formats of axes, names and readouts
//   chartWeekdays(style)     Monday first, in the locale ('short', 'long'); chartHour(h) "14:00" in every locale
//   chartValueFormat(p)      p.format (Intl options or a function) as a function; '—' for a value that is no number
//   chartTickFormat(p, step, top)  axis ticks: p.tickFormat, else p.format, else as many decimals as the step needs,
//                            compact from 1,000 (not for percent)
// Numbers
//   chartNum(v)              a number that is not NaN; chartSum(a) the sum of the positive numbers
//   chartWhole(p, values)    whether a Line or Bar axis counts in whole steps (no percent, no format function,
//                            every value whole)
//   chartStep(span, count, integer)  a nice step on 1, 2, 2.5 and 5; whole for counts (no 2.5 below 10)
//   chartScale(lo, hi, count, integer)  { min, max, step, ticks }: the nice domain around lo and hi
//   chartDateTicks(dates, plotWidth, dayFormat)  the LineChart's date ticks: indices stepping back from the last
//                            point at one even stride (1, 2, 7, 14 … days for daily data), as far apart as the
//                            labels need, at most seven
// Measuring text (labels that must fit)
//   chartMeasure(text, size, weight)  the width in the interface font (--font-sans) on a canvas; where there is no
//                            canvas (happy-dom) length × size × 0.56, as the system's fallback
//   chartFit(text, max, size, weight)  cut with an ellipsis to fit max, or '' when not even one letter fits
// Paths (SVG d strings)
//   chartLinePath(vals, X, Y), chartAreaPath(vals, X, Y, base): a gap (no number) lifts the pen
//   chartBarPath(x, y, w, ht, r, vertical, square): 4px round at the data end, square at the baseline
//   chartPolar(c, r, deg), chartArc(c, r0, r1, a0, a1): donut slices; a whole ring is drawn as two halves
// Keys
//   chartLineKey(key, i, n)  the LineChart's move: arrows one point, Page Up and Page Down seven, Home and End,
//                            clamped at the ends (time does not wrap); null for another key. BarChart and DonutChart
//                            wrap with helpers.ts's rovingIndex instead
// Templates (HTML, beside the plot)
//   chartLegend(items)       ul.sdr-chart__legend, "Legend": one key per series (line, dash, rect) and its name
//   chartTip(p)              the readout, aria-hidden: title, rows (key, value, label; a total row), foot; nothing
//                            without an anchor. placeChartTip(el, p) places it as the system's layout effect does,
//                            from its offsetWidth and offsetHeight: call it in updated() after every render
// Controllers (on the chart's LitElement, which keeps the state, as the system's hooks)
//   ChartWidth               the plot's width (chartUseWidth): bind ref(width.ref) on .sdr-chart__plot; it reads
//                            Math.floor(getBoundingClientRect().width) after the render that drew the plot, again in
//                            the next frame after the ResizeObserver reports, or on window resize without one; it
//                            lets go when the plot goes or the element leaves the page. width is 0 until measured
//   ChartFonts               draws again once document.fonts.ready resolves (labels are measured in Mona Sans)
//   ChartMarks               one tab stop and a roving tabindex over the marks, the readout on hover and on focus
//                            (chartUseMarks). Call frame(p, count, start) at the start of each render, with
//                            p.active and p.onActiveChange (controlled exactly as useControlled) or {}; then read
//                            active, tabStop, keyboard (the focus came by keyboard: draw the ring), showTip, hushed;
//                            bind ref(marks.svg) and @pointerleave=${marks.leave} on the svg, @focusout=${marks.blur}
//                            on the HTML element around it (.sdr-chart__plot), never on the svg: Chrome makes an SVG
//                            element with a focus-type listener a tab stop of its own (R10f; React listens at its
//                            root, so the system's svg has none; shared-rules.dom.ts holds every port to it). On each
//                            mark data-mark=${i}, tabindex=${i === marks.tabStop ? 0 : -1},
//                            @pointerenter=${() => marks.enter(i)} and @focus=${e => marks.focus(i, e)}; in the key
//                            handler marks.escape(e) for Escape and marks.move(i) to focus a mark. The handlers read
//                            what the last render drew, as React's closures do. A pointer's enter or leave is drawn,
//                            and read by the handlers, only in the next task (afterTask), as React renders its
//                            continuous lane: a tap (pointerleave, then the focus, in one task) ends as the system's
//                            does (R10f, found in Chrome)
//   afterTask(fn)            fn in the next task, as React's scheduler picks it: setImmediate where there is one (Node),
//                            else a MessageChannel message (browsers), else a timeout
//
// Measuring is the system's: the canvas context and the font family are looked up once and kept for the page, as in
// the bundle. A test that gives the page a canvas keeps it for every later chart in the process.
//
// Colours only through the sdr-series-- classes and the chart tokens in components.css; text is ink, ink-muted or
// ink-subtle there, never a series colour.
//
// One quirk of the system kept as it is: a colour is looked up in an object, so a name the object inherits
// ('constructor', 'toString') counts as a named colour and gives sdr-series--constructor (reported, not fixed here).
// ---------------------------------------------------------------------------

import { html, nothing, type ReactiveController, type ReactiveControllerHost, type TemplateResult } from 'lit';
import { createRef } from 'lit/directives/ref.js';

import { cx, format, node, present } from './helpers.js';

// ---- Colour ----

/** The identity slots, in their fixed order: chart-1 (Email), chart-2 (LinkedIn), chart-3 (Phone), chart-5, chart-6. */
export const CHART_SLOTS = ['chart-1', 'chart-2', 'chart-3', 'chart-5', 'chart-6'] as const;

/** A series colour as its token (chart-4 the de-emphasis grey, chart-other the "Other" grey, muted a single series). */
export type ChartInk = 'chart-1' | 'chart-2' | 'chart-3' | 'chart-4' | 'chart-5' | 'chart-6' | 'chart-other' | 'muted';
/** What a caller may name: an ink, or 'other' for chart-other. */
export type ChartColor = ChartInk | 'other';

/** The system's CHART_INKS: looked up as an object, as there. */
const INKS: Record<string, number> = {
  'chart-1': 1,
  'chart-2': 1,
  'chart-3': 1,
  'chart-4': 1,
  'chart-5': 1,
  'chart-6': 1,
  'chart-other': 1,
  muted: 1,
};

/** A colour the caller names wins ('other' is chart-other); otherwise the series' position in the data. */
export function chartInk(color: unknown, i: number): ChartInk {
  if (color === 'other') return 'chart-other';
  if (INKS[color as string]) return color as ChartInk;
  return i < CHART_SLOTS.length ? (CHART_SLOTS[i] as ChartInk) : 'chart-other';
}

/** The class that colours a mark with its series token. */
export function chartSeries(ink: string): string {
  return `sdr-series--${ink}`;
}

// ---- Words ----

/** Axis dates: "Oct 28". */
export const CHART_DAY: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' };
/** A mark's name: "Wednesday, October 28". */
export const CHART_DAY_LONG: Intl.DateTimeFormatOptions = { weekday: 'long', day: 'numeric', month: 'long' };
/** The readout and the table: "Wed, Oct 28". */
export const CHART_DAY_TIP: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short' };
/** Hours read 14:00, as the brand book asks, in every locale. */
export const CHART_CLOCK: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' };

/** The chart's name as text: a title given as a template names the figure through its heading, not here. */
export function chartName(title: unknown, label: unknown, fallback: string): unknown {
  return typeof title === 'string' && title ? title : label || fallback;
}

/** The weekdays, Monday first, in the reader's locale. */
export function chartWeekdays(style: 'long' | 'short' | 'narrow'): string[] {
  const a: string[] = [];
  for (let i = 0; i < 7; i++) a.push(format.date(new Date(2024, 0, 1 + i), { weekday: style }));
  return a;
}

/** An hour of the day as 14:00. */
export function chartHour(hr: number): string {
  return format.time(new Date(2024, 0, 1, hr), CHART_CLOCK);
}

/** Values are Intl.NumberFormat options in the reader's locale, or the caller's function. */
export type ChartFormat = Intl.NumberFormatOptions | ((value: number) => string);

/** The chart's value format as a function: a number in the locale, '—' for anything else. */
export function chartValueFormat(p: { format?: ChartFormat | null }): (v: unknown) => string {
  if (typeof p.format === 'function') return p.format as (v: unknown) => string;
  const o = p.format || undefined;
  return v => (chartNum(v) ? format.number(v, o) : '—');
}

/** Ticks carry as many decimals as their step needs, and go compact from 1,000 (1K, 2.5K) in the reader's locale. */
export function chartTickFormat(
  p: { format?: ChartFormat | null; tickFormat?: ((value: number) => string) | null },
  step: number,
  top: number,
): (v: number) => string {
  if (typeof p.tickFormat === 'function') return p.tickFormat;
  if (typeof p.format === 'function') return p.format;
  const o: Intl.NumberFormatOptions = p.format || {};
  const unitStep = o.style === 'percent' ? step * 100 : step;
  const dec = (String(+unitStep.toFixed(6)).split('.')[1] || '').length;
  let opts: Intl.NumberFormatOptions = Object.assign({}, o, { minimumFractionDigits: 0, maximumFractionDigits: dec });
  if (o.style !== 'percent' && Math.abs(top) >= 1000)
    opts = Object.assign({}, o, { notation: 'compact', minimumFractionDigits: 0, maximumFractionDigits: 1 });
  return v => format.number(v, opts);
}

// ---- Numbers ----

/** A number that is not NaN. */
export function chartNum(v: unknown): v is number {
  // biome-ignore lint/suspicious/noSelfCompare: NaN is the one number that is not itself, as the system tests it
  return typeof v === 'number' && v === v;
}

/** The sum of the positive numbers. */
export function chartSum(a: readonly unknown[]): number {
  let t = 0;
  for (const x of a) if (chartNum(x) && x > 0) t += x;
  return t;
}

/** Whether a LineChart's or BarChart's value axis counts in whole steps: no percent, no format function, all whole. */
export function chartWhole(p: { format?: ChartFormat | null }, values: readonly number[]): boolean {
  const f = p.format;
  return (
    typeof f !== 'function' &&
    !(f && (f as Intl.NumberFormatOptions).style === 'percent') &&
    values.every(v => Math.round(v) === v)
  );
}

/** Axis steps on 1, 2, 2.5 and 5; whole numbers for counts. */
export function chartStep(span: number, count: number, integer?: boolean): number {
  const raw = span / Math.max(1, count);
  if (integer && raw <= 1) return 1;
  const mag = 10 ** Math.floor(Math.log(raw) / Math.LN10);
  const f = raw / mag;
  const nice = f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 && !(integer && mag < 10) ? 2.5 : f <= 5 ? 5 : 10;
  return nice * mag;
}

/** The nice domain around lo and hi, with its ticks. */
export interface ChartScale {
  min: number;
  max: number;
  step: number;
  ticks: number[];
}

export function chartScale(lo: number, hi: number, count: number, integer?: boolean): ChartScale {
  if (!(hi > lo)) hi = lo + 1;
  const step = chartStep(hi - lo, count, integer);
  const min = Math.floor(lo / step + 1e-9) * step;
  const max = Math.ceil(hi / step - 1e-9) * step;
  const ticks: number[] = [];
  for (let k = Math.round(min / step); k <= Math.round(max / step); k++) ticks.push(+(k * step).toFixed(10));
  return { min, max, step, ticks };
}

/**
 * The LineChart's date ticks, as indices into dates: they step back from the latest point at an even stride (a
 * week for daily data), as far apart as the labels need, at most seven. Every gap is the stride: no extra tick at
 * the first date. plotWidth is the plot's width without the margins; dayFormat the axis format (CHART_DAY).
 */
export function chartDateTicks(
  dates: readonly Date[],
  plotWidth: number,
  dayFormat?: Intl.DateTimeFormatOptions | null,
): number[] {
  const n = dates.length;
  if (!n) return [];
  const last = dates[n - 1] as Date;
  const span = n > 1 ? last.getTime() - (dates[0] as Date).getTime() : 0;
  const lw = chartMeasure(format.date(last, dayFormat || CHART_DAY), 12) + 24;
  const per = n > 1 ? plotWidth / (n - 1) : plotWidth;
  const minStride = Math.max(1, Math.ceil(lw / per), Math.ceil((n - 1) / 6));
  const daily = n > 1 && Math.abs(span / (n - 1) / 864e5 - 1) < 0.2;
  const stride =
    (daily ? [1, 2, 7, 14, 28, 56, 91] : [1, 2, 3, 4, 6, 8, 12, 13, 26, 52]).filter(s => s >= minStride)[0] ||
    minStride;
  const xt: number[] = [];
  for (let ix = n - 1; ix >= 0; ix -= stride) xt.unshift(ix);
  return xt;
}

// ---- Measuring text ----

type Context = { font: string; measureText(text: string): { width: number } };

let context: Context | null = null;
let family: string | null = null;

/** Text width in the interface font, for labels that must fit (axis ticks, category names, end labels). */
export function chartMeasure(text: unknown, size: number, weight?: number): number {
  const s = String(text == null ? '' : text);
  try {
    if (family == null)
      family = getComputedStyle(document.documentElement).getPropertyValue('--font-sans').trim() || 'sans-serif';
    if (!context) context = document.createElement('canvas').getContext('2d') as Context | null;
    // The system writes the font on a missing context and lands in its fallback: the same width.
    if (!context) return s.length * size * 0.56;
    context.font = `${weight || 400} ${size}px ${family}`;
    return context.measureText(s).width;
  } catch {
    return s.length * size * 0.56;
  }
}

/** A label that does not fit is cut with an ellipsis; the whole name stays in the mark's name, the readout, the table. */
export function chartFit(text: unknown, max: number, size: number, weight?: number): string {
  const s = String(text == null ? '' : text);
  if (chartMeasure(s, size, weight) <= max) return s;
  let lo = 0;
  let hi = s.length;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (chartMeasure(`${s.slice(0, mid).replace(/\s+$/, '')}…`, size, weight) <= max) lo = mid;
    else hi = mid - 1;
  }
  return lo ? `${s.slice(0, lo).replace(/\s+$/, '')}…` : '';
}

// ---- Paths ----

/** A line through the values; a value that is no number lifts the pen. */
export function chartLinePath(vals: readonly unknown[], X: (i: number) => number, Y: (v: number) => number): string {
  let d = '';
  let pen = false;
  for (let i = 0; i < vals.length; i++) {
    const v = vals[i];
    if (!chartNum(v)) {
      pen = false;
      continue;
    }
    d += `${pen ? 'L' : 'M'}${X(i).toFixed(1)} ${Y(v).toFixed(1)}`;
    pen = true;
  }
  return d;
}

/** The area under each run of two or more values, closed to base. */
export function chartAreaPath(
  vals: readonly unknown[],
  X: (i: number) => number,
  Y: (v: number) => number,
  base: number,
): string {
  let d = '';
  let run: number[] = [];
  const flush = () => {
    if (run.length > 1)
      d += `M${X(run[0] as number).toFixed(1)} ${base.toFixed(1)}${run
        .map(i => `L${X(i).toFixed(1)} ${Y(vals[i] as number).toFixed(1)}`)
        .join('')}L${X(run[run.length - 1] as number).toFixed(1)} ${base.toFixed(1)}Z`;
    run = [];
  };
  for (let i = 0; i < vals.length; i++) {
    if (chartNum(vals[i])) run.push(i);
    else flush();
  }
  flush();
  return d;
}

/** A bar: 4px rounded at its data end, square at the baseline. Horizontal grows right from x; vertical grows up to y. */
export function chartBarPath(
  x: number,
  y: number,
  w: number,
  ht: number,
  r: number,
  vertical?: boolean,
  square?: boolean,
): string {
  if (!(w > 0) || !(ht > 0)) return '';
  if (square) return `M${x} ${y}h${w}v${ht}h${-w}Z`;
  if (!vertical) {
    r = Math.min(r, w, ht / 2);
    return `M${x} ${y}H${x + w - r}A${r} ${r} 0 0 1 ${x + w} ${y + r}V${y + ht - r}A${r} ${r} 0 0 1 ${x + w - r} ${y + ht}H${x}Z`;
  }
  r = Math.min(r, ht, w / 2);
  return `M${x} ${y + ht}V${y + r}A${r} ${r} 0 0 1 ${x + r} ${y}H${x + w - r}A${r} ${r} 0 0 1 ${x + w} ${y + r}V${y + ht}Z`;
}

/** The point at deg degrees on a circle around (c, c). */
export function chartPolar(c: number, r: number, deg: number): string {
  const t = (deg * Math.PI) / 180;
  return `${(c + r * Math.cos(t)).toFixed(2)} ${(c + r * Math.sin(t)).toFixed(2)}`;
}

/** A ring segment from a0 to a1 degrees between the radii r0 and r1; a whole ring is two halves. */
export function chartArc(c: number, r0: number, r1: number, a0: number, a1: number): string {
  if (a1 - a0 >= 359.99) return chartArc(c, r0, r1, a0, a0 + 180) + chartArc(c, r0, r1, a0 + 180, a0 + 359.99);
  const large = a1 - a0 > 180 ? 1 : 0;
  return `M${chartPolar(c, r1, a0)}A${r1} ${r1} 0 ${large} 1 ${chartPolar(c, r1, a1)}L${chartPolar(c, r0, a1)}A${r0} ${r0} 0 ${large} 0 ${chartPolar(c, r0, a0)}Z`;
}

// ---- Keys ----

/** The LineChart's keys: one point, seven with Page Up and Page Down, the ends; clamped, time does not wrap. */
export function chartLineKey(key: string, i: number, n: number): number | null {
  if (key === 'ArrowRight' || key === 'ArrowDown') return Math.min(n - 1, i + 1);
  if (key === 'ArrowLeft' || key === 'ArrowUp') return Math.max(0, i - 1);
  if (key === 'PageDown') return Math.min(n - 1, i + 7);
  if (key === 'PageUp') return Math.max(0, i - 7);
  if (key === 'Home') return 0;
  if (key === 'End') return n - 1;
  return null;
}

// ---- Legend and readout ----

/** One series in the legend: its key mirrors the mark (line, dashed line, swatch). */
export interface ChartLegendItem {
  key: 'line' | 'dash' | 'rect';
  ink: string;
  label: unknown;
}

/** Legend: always there for two or more series. */
export function chartLegend(items: readonly ChartLegendItem[]): TemplateResult {
  return html`<ul class="sdr-chart__legend" aria-label="Legend">${items.map(
    it =>
      html`<li class="sdr-chart__legend-item"><span class=${cx('sdr-chart__key', `sdr-chart__key--${it.key}`, chartSeries(it.ink))} aria-hidden="true"></span><span>${node(it.label)}</span></li>`,
  )}</ul>`;
}

/** One row of the readout: the value leads, the series' name follows, keyed by its colour (or no key). */
export interface ChartTipRow {
  /** The series' ink; without it the row has an empty key. */
  ink?: string | null;
  /** The key's shape: 'line' (the default), 'dash' or 'rect'. */
  key?: string;
  value: unknown;
  label?: unknown;
  /** A total under a line. */
  total?: boolean;
}

/** Where the readout points, in the plot's pixels: below is the mark's bottom, where it may flip to. */
export interface ChartTipAnchor {
  x: number;
  y: number;
  below?: number | null;
}

export interface ChartTipProps {
  anchor?: ChartTipAnchor | null;
  /** Beside the anchor (a crosshair), else above it. */
  side?: boolean;
  /** The plot's width: the readout stays inside it. */
  width: number;
  title?: unknown;
  rows: readonly ChartTipRow[];
  foot?: unknown;
}

/**
 * The readout: values lead, series names follow, each keyed by a short stroke (or a swatch) of its colour. Visual
 * only (aria-hidden): the focused mark's name says the same to a screen reader. Nothing without an anchor.
 */
export function chartTip(p: ChartTipProps): TemplateResult | typeof nothing {
  if (!p.anchor) return nothing;
  return html`<div class="sdr-chart__tip" aria-hidden="true">${
    present(p.title) ? html`<div class="sdr-chart__tip-title">${node(p.title)}</div>` : nothing
  }${p.rows.map(
    r =>
      html`<div class=${cx('sdr-chart__tip-row', r.total && 'sdr-chart__tip-row--total')}><span class=${cx(
        'sdr-chart__key',
        `sdr-chart__key--${r.ink ? r.key || 'line' : 'none'}`,
        r.ink && chartSeries(r.ink),
      )}></span><span class="sdr-chart__tip-value">${node(r.value)}</span>${
        present(r.label) ? html`<span class="sdr-chart__tip-label">${node(r.label)}</span>` : nothing
      }</div>`,
  )}${present(p.foot) ? html`<div class="sdr-chart__tip-foot">${node(p.foot)}</div>` : nothing}</div>`;
}

/**
 * Places the readout as the system's layout effect does after every render: from its own offsetWidth and
 * offsetHeight, 12px beside the anchor (flipped left when it would leave the plot), or 8px above it (below the
 * mark when more than half of it would leave the top), always inside the plot's width.
 */
export function placeChartTip(
  el: HTMLElement | null | undefined,
  p: { anchor?: ChartTipAnchor | null; side?: boolean; width: number },
): void {
  const a = p.anchor;
  if (!el || !a) return;
  const w = el.offsetWidth;
  const ht = el.offsetHeight;
  const W = p.width;
  const gap = 12;
  let x: number;
  let y: number;
  if (p.side) {
    x = a.x + gap;
    if (x + w > W) x = a.x - gap - w;
    if (x < 0) x = Math.max(0, Math.min(W - w, a.x - w / 2));
    y = a.y;
  } else {
    x = Math.max(0, Math.min(W - w, a.x - w / 2));
    y = a.y - ht - 8;
    if (y < -ht / 2 && a.below != null) y = a.below + 8;
  }
  el.style.left = `${Math.round(x)}px`;
  el.style.top = `${Math.round(y)}px`;
}

// ---- Controllers ----

/** The chart's element: a LitElement (hasUpdated, isConnected). */
type Host = ReactiveControllerHost & HTMLElement & { readonly hasUpdated?: boolean };

/**
 * The plot's own width, read before paint and again whenever its column changes size (the system's chartUseWidth):
 * bind ref(width.ref) on the plot. It measures after the render that drew the plot (or a new one), in the next frame
 * after the ResizeObserver reports, or at once on window resize where there is no ResizeObserver; it lets go of the
 * observer and the frame when the plot goes or the element leaves the page. width is 0 until it has measured.
 */
export class ChartWidth implements ReactiveController {
  /** The measured width in whole px; 0 before the first measure. */
  width = 0;
  /** The plot the ref holds now (the system's state that its callback ref sets). */
  private node: Element | null = null;
  /** The plot the effect watches. */
  private watched: Element | null = null;
  private stop: (() => void) | null = null;

  private readonly host: Host;

  constructor(host: Host) {
    this.host = host;
    host.addController(this);
  }

  /** The callback for ref(): the plot's element, or undefined when it goes. */
  readonly ref = (el?: Element): void => {
    const next = el ?? null;
    if (next === this.node) return;
    this.node = next;
    // Leaving the page lets go in hostDisconnected; nothing to draw for that (React draws nothing on unmount).
    if (this.host.isConnected) this.host.requestUpdate();
  };

  hostUpdated(): void {
    this.watch();
  }

  hostDisconnected(): void {
    this.unwatch();
  }

  /** The system's layout effect on the plot: measure now, then on resize. */
  private watch() {
    if (this.node === this.watched) return;
    this.unwatch();
    const plot = this.node;
    this.watched = plot;
    if (!plot) return;
    let frame = 0;
    const read = () => {
      const x = Math.floor(plot.getBoundingClientRect().width);
      if (Object.is(x, this.width)) return;
      this.width = x;
      this.host.requestUpdate();
    };
    read();
    if (typeof ResizeObserver !== 'function') {
      window.addEventListener('resize', read);
      this.stop = () => window.removeEventListener('resize', read);
      return;
    }
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(read);
    });
    ro.observe(plot);
    this.stop = () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
    };
  }

  private unwatch() {
    const stop = this.stop;
    this.stop = null;
    this.watched = null;
    stop?.();
  }
}

/**
 * Labels are measured in Mona Sans: once the fonts have loaded, the chart draws again (the system's chartUseFonts,
 * an effect on mount). Back in the page after leaving it, it waits for them again, as a new mount would.
 */
export class ChartFonts implements ReactiveController {
  private live: { on: boolean } | null = null;

  private readonly host: Host;

  constructor(host: Host) {
    this.host = host;
    host.addController(this);
  }

  hostConnected(): void {
    // A first connection waits for the first render, as an effect runs after it.
    if (this.host.hasUpdated) this.arm();
  }

  hostUpdated(): void {
    if (!this.live && this.host.isConnected) this.arm();
  }

  hostDisconnected(): void {
    if (this.live) this.live.on = false;
    this.live = null;
  }

  private arm() {
    const live = { on: true };
    this.live = live;
    const fonts = (document as { fonts?: { ready?: Promise<unknown> } }).fonts;
    if (fonts?.ready)
      fonts.ready.then(() => {
        if (live.on) this.host.requestUpdate();
      });
  }
}

/**
 * Runs fn in the next task, the way React's scheduler renders an update of its continuous lane (a pointer's enter,
 * leave or move): setImmediate where there is one (Node), else a MessageChannel message (browsers), else a timeout.
 * Chrome sends a tap's pointerleave, focus and click in one task with only microtasks between them, so a handler
 * there still sees what React drew before the pointer's update (R10f, found in Chrome).
 */
export function afterTask(fn: () => void): void {
  const g = globalThis as { setImmediate?: (f: () => void) => unknown };
  if (typeof g.setImmediate === 'function') g.setImmediate(fn);
  else if (typeof MessageChannel !== 'undefined') {
    const c = new MessageChannel();
    c.port1.onmessage = () => {
      c.port1.close();
      fn();
    };
    c.port2.postMessage(null);
  } else setTimeout(fn, 0);
}

/** What makes the active mark controlled: the index the caller holds, and the callback for every change. */
export interface ChartMarksProps {
  /** The active mark (a crosshair's date): given, it is controlled; null for none. */
  active?: number | null;
  onActiveChange?: ((index: number | null) => void) | null;
}

type Owner = 'pointer' | 'focus' | null;

/** What the last render drew: React's handlers close over it. */
interface Drawn {
  p: ChartMarksProps;
  act: number | null | undefined;
  own: Owner;
  kbd: boolean;
  hush: boolean;
}

/**
 * What every chart shares (the system's chartUseMarks): one tab stop, its marks under a roving tabindex, the same
 * readout on hover and on focus. Escape hides the readout until the next move. A pointer shows it without the ring;
 * the keyboard draws the ring. See the header for how a chart binds it.
 */
export class ChartMarks implements ReactiveController {
  /** The svg that holds the marks: bind it with ref(marks.svg). */
  readonly svg = createRef<Element>();
  private ownActive: number | null = null;
  private own: Owner = null;
  private kbd = false;
  private hush = false;
  private rove: number | null = null;
  private cur: number | null = null;
  private drawn: Drawn = { p: {}, act: null, own: null, kbd: false, hush: false };
  /**
   * A pointer's update waits for the next task (React's continuous lane): until then every render draws, and every
   * handler reads, what was drawn before it; the props are taken as they come.
   */
  private lagging = false;

  private readonly host: ReactiveControllerHost;

  constructor(host: ReactiveControllerHost) {
    this.host = host;
    host.addController(this);
  }

  hostConnected(): void {}

  /** At the start of each render: the props that control it, the number of marks, and the first tab stop. */
  frame(p: ChartMarksProps, count: number, start: number): void {
    const act = this.lagging ? this.drawn.act : p.active !== undefined ? p.active : this.ownActive;
    this.cur = act != null && act >= 0 && act < count ? act : null;
    if (this.cur != null) this.rove = this.cur;
    if (this.rove == null || this.rove >= count) this.rove = start;
    this.drawn = this.lagging ? { ...this.drawn, p } : { p, act, own: this.own, kbd: this.kbd, hush: this.hush };
  }

  /** The active mark, within the marks; null for none. */
  get active(): number | null {
    return this.cur;
  }

  /** The mark with tabindex 0: the active one, else the last one that was, else the start. */
  get tabStop(): number {
    return this.rove as number;
  }

  /** Escape hid the readout (until the next move). */
  get hushed(): boolean {
    return this.drawn.hush;
  }

  /** The focus came by keyboard: draw the ring. */
  get keyboard(): boolean {
    return this.drawn.kbd && this.drawn.own === 'focus';
  }

  /** Whether to show the readout for the active mark. */
  get showTip(): boolean {
    return this.drawn.own != null && this.cur != null && !this.drawn.hush;
  }

  /** The pointer is on mark i. */
  enter(i: number): void {
    this.show(i, this.drawn.own === 'focus' ? 'focus' : 'pointer', false);
    this.later();
  }

  /** Mark i has the focus: by keyboard unless :focus-visible says otherwise. */
  focus(i: number, e: Event): void {
    let keyboard = true;
    try {
      keyboard = (e.target as Element).matches(':focus-visible');
    } catch {
      /* older browsers: treat as keyboard */
    }
    this.show(i, 'focus', keyboard);
  }

  /** The pointer left the chart: a readout it showed goes. */
  readonly leave = (): void => {
    if (this.drawn.own !== 'pointer') return;
    this.clear();
    this.later();
  };

  /** The focus left a mark: unless it went to another mark of the chart, the readout goes. */
  readonly blur = (e: FocusEvent): void => {
    const svg = this.svg.value;
    if (!svg?.contains(e.relatedTarget as Node | null)) this.clear();
  };

  /** Escape: hides a readout that shows, and keeps the key from a dialog around the chart. */
  readonly escape = (e: Event): void => {
    if (this.drawn.own != null && !this.drawn.hush) {
      e.stopPropagation();
      this.hush = true;
      this.host.requestUpdate();
    }
  };

  /** Focuses mark i (its focus shows its readout). */
  move(i: number): void {
    const m = this.svg.value?.querySelector<HTMLElement | SVGElement>(`[data-mark="${i}"]`);
    if (m) m.focus();
  }

  /** A pointer's update: drawn in the next task, as React renders its continuous lane. */
  private later() {
    if (this.lagging) return;
    this.lagging = true;
    afterTask(() => {
      this.lagging = false;
      this.host.requestUpdate();
    });
  }

  private setActive(v: number | null) {
    const p = this.drawn.p;
    if (p.active === undefined) this.ownActive = v;
    if (p.onActiveChange) p.onActiveChange(v);
  }

  private show(i: number, how: Owner, keyboard: boolean) {
    this.hush = false;
    this.own = how;
    this.kbd = !!keyboard;
    if (this.drawn.act !== i) this.setActive(i);
    this.host.requestUpdate();
  }

  private clear() {
    this.own = null;
    this.kbd = false;
    if (this.drawn.act != null) this.setActive(null);
    this.host.requestUpdate();
  }
}
